import { useEffect, useState } from 'react';
import { CoreDrawer } from '@/components/common/CoreDrawer';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ProviderIcon } from '@/components/agent/core/ProviderIcon';
import { useLlmModels } from '@/hooks/voice/useLlmModels';
import { BULK_UPDATE_TEMPLATE_GROUPS, BULK_UPDATE_TEMPLATES, PHONE_GOALS_TEMPLATE_ID, SILENCE_TIMEOUT_TEMPLATE_ID } from './BulkUpdateTemplates';
import { BulkUpdateTemplate, PendingBulkUpdate } from '@/types/agent/bulk-update';

const SELECTED_ONLY_SCOPE = { applyToAll: false, includeDemo: false, includeMarketplace: false };

interface BulkUpdateSheetProps {
  isOpen: boolean;
  selectedAgentIds: Set<string>;
  onClose: () => void;
  onApplyUpdates: (pending: PendingBulkUpdate) => void;
  isApplying?: boolean;
}

export const BulkUpdateSheet = ({
  isOpen,
  selectedAgentIds,
  onClose,
  onApplyUpdates,
  isApplying = false,
}: BulkUpdateSheetProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState<BulkUpdateTemplate | null>(null);
  const [customUpdates, setCustomUpdates] = useState<Record<string, any>>({});
  const [scope, setScope] = useState(SELECTED_ONLY_SCOPE);
  const { data: llmModels = [] } = useLlmModels();

  useEffect(() => {
    if (isOpen) return;
    setSelectedTemplate(null);
    setCustomUpdates({});
    setScope(SELECTED_ONLY_SCOPE);
  }, [isOpen]);

  const handleTemplateSelect = (template: BulkUpdateTemplate) => {
    setSelectedTemplate(template);
    setCustomUpdates(JSON.parse(JSON.stringify(template.updates)));
  };

  const handleCustomUpdate = (fieldPath: string, value: any) => {
    const keys = fieldPath.split('.');
    let current: any = { ...customUpdates };
    let target = current;

    for (let i = 0; i < keys.length - 1; i++) {
      target[keys[i]] = target[keys[i]] || {};
      target = target[keys[i]];
    }

    target[keys[keys.length - 1]] = value;
    setCustomUpdates(current);
  };

  const llmValue = customUpdates.settings?.conversation_config?.agent?.prompt?.llm || '';
  const voiceId = customUpdates.settings?.conversation_config?.tts?.voice_id || '';
  const isMissingValue =
    (selectedTemplate?.id === 'change-llm' && !llmValue) || (selectedTemplate?.id === 'change-voice' && !voiceId.trim());
  const hasTarget = scope.applyToAll || selectedAgentIds.size > 0;
  const targetLabel = scope.applyToAll
    ? 'All genies on the platform'
    : `${selectedAgentIds.size} agent${selectedAgentIds.size !== 1 ? 's' : ''}`;
  const temperature = customUpdates.settings?.conversation_config?.agent?.prompt?.temperature ?? 0.7;
  const reportMode = customUpdates.report_mode || '';
  const callLimit = customUpdates.settings?.platform_settings?.call_limits?.daily_limit || '';
  const maxDuration = customUpdates?.settings?.conversation_config?.conversation?.max_duration_seconds ?? 1800;

  return (
    <CoreDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Bulk Update Agents"
      description={
        scope.applyToAll
          ? 'Apply updates to all genies on the platform'
          : `Apply updates to ${selectedAgentIds.size} selected agent${selectedAgentIds.size !== 1 ? 's' : ''}`
      }
    >
      <div className="flex flex-col min-h-full">
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] items-start gap-6 py-4">
          <div className="order-2 lg:order-1 lg:sticky lg:top-4 space-y-6">
            {/* Scope */}
            <div className="space-y-3 p-4 rounded-lg border border-gray-200">
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={scope.applyToAll}
                  onCheckedChange={(checked) => setScope({ ...SELECTED_ONLY_SCOPE, applyToAll: checked === true })}
                />
                <span className="text-sm font-semibold text-gray-700">Apply to all genies on the platform</span>
              </label>
              {scope.applyToAll && (
                <div className="space-y-2 pl-7">
                  <p className="text-xs text-gray-500">
                    Includes customers' genies and ignores the agents selected in the list. Demo and marketplace genies are left out unless you tick them.
                  </p>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={scope.includeDemo}
                      onCheckedChange={(checked) => setScope({ ...scope, includeDemo: checked === true })}
                    />
                    <span className="text-sm text-gray-600">Include demo genies</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={scope.includeMarketplace}
                      onCheckedChange={(checked) => setScope({ ...scope, includeMarketplace: checked === true })}
                    />
                    <span className="text-sm text-gray-600">Include marketplace genies</span>
                  </label>
                </div>
              )}
            </div>

            {/* Custom Update Fields */}
            {selectedTemplate ? (
              <div className="space-y-4 border-t pt-6">
                <h3 className="font-semibold text-gray-900">Configure Update</h3>

                {selectedTemplate.id === 'change-llm' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Select LLM Model</label>
                    <Select
                      value={llmValue}
                      onValueChange={(value) => handleCustomUpdate('settings.conversation_config.agent.prompt.llm', value)}
                    >
                      <SelectTrigger className="w-full border-gray-300 focus:ring-2 focus:ring-genie-primary focus:border-transparent h-auto py-2">
                        <SelectValue placeholder="Choose a model..." />
                      </SelectTrigger>
                      <SelectContent>
                        {llmModels.map((model) => (
                          <SelectItem key={model.id} value={model.id}>
                            <span className="flex items-center gap-2">
                              <ProviderIcon provider={model.provider} />
                              {model.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {selectedTemplate.id === 'change-voice' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Voice ID</label>
                    <input
                      type="text"
                      value={voiceId}
                      onChange={(e) => handleCustomUpdate('settings.conversation_config.tts.voice_id', e.target.value)}
                      placeholder="Enter voice ID"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-genie-primary focus:border-transparent"
                    />
                  </div>
                )}

                {selectedTemplate.id === 'change-temperature' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Temperature: {temperature.toFixed(1)}</label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={temperature}
                      onChange={(e) => handleCustomUpdate('settings.conversation_config.agent.prompt.temperature', parseFloat(e.target.value))}
                      className="w-full"
                    />
                    <p className="text-xs text-gray-600">0 = deterministic, 1 = creative</p>
                  </div>
                )}

                {selectedTemplate.id === 'change-report-mode' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Report Mode</label>
                    <select
                      value={reportMode}
                      onChange={(e) => handleCustomUpdate('report_mode', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-genie-primary focus:border-transparent"
                    >
                      <option value="auto">Auto (AI decides)</option>
                      <option value="every_conversation">Every conversation</option>
                      <option value="digest">Scheduled digest</option>
                      <option value="escalation_only">Only when attention needed</option>
                      <option value="off">Off</option>
                    </select>
                  </div>
                )}

                {selectedTemplate.id === 'update-call-limit' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Daily Call Limit</label>
                    <input
                      type="number"
                      value={callLimit}
                      onChange={(e) => handleCustomUpdate('settings.platform_settings.call_limits.daily_limit', parseInt(e.target.value))}
                      placeholder="Enter limit"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-genie-primary focus:border-transparent"
                    />
                  </div>
                )}

                {selectedTemplate.id === 'set-max-duration' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Max Duration (seconds)</label>
                    <input
                      type="number"
                      min={60}
                      value={maxDuration}
                      onChange={(e) => handleCustomUpdate('settings.conversation_config.conversation.max_duration_seconds', parseInt(e.target.value))}
                      placeholder="Enter seconds"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-genie-primary focus:border-transparent"
                    />
                    <p className="text-xs text-gray-600">{Math.floor(maxDuration / 60)}m {maxDuration % 60}s</p>
                  </div>
                )}

                {selectedTemplate.id === 'enable-text-only' && (
                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-900">
                      Text-only mode will be enabled for all selected agents. Voice features will be disabled.
                    </p>
                  </div>
                )}

                {selectedTemplate.id === SILENCE_TIMEOUT_TEMPLATE_ID && (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-900">
                      Genies below 5 minutes are raised to 5 minutes. Longer timeouts and genies with the timeout off stay as they are. Only this setting changes, and it is safe to run again.
                    </p>
                  </div>
                )}

                {selectedTemplate.id === PHONE_GOALS_TEMPLATE_ID && (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-900">
                      Phone calls start by asking Help Genie for the genie's goals, and the genie's stored prompt never changes. Only genies with their own phone number are updated: genies without one and Listener Genies are skipped, even when applying to all. Applying to all also skips genies that already have this. It is safe to run again.
                    </p>
                  </div>
                )}

                {selectedTemplate.id === 'enable-multilingual' && (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-900">
                      Language presets for all languages supported by each agent's TTS model will be enabled, allowing automatic language detection and switching. Genies on a model without language presets switch to Multilingual v2.
                    </p>
                  </div>
                )}

                {selectedTemplate.id === 'attach-related-media-tool' && (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-900">
                      The Links &amp; Images tool is added alongside each agent's existing tools, and client tool events are enabled so the browser receives its calls.
                    </p>
                  </div>
                )}

                {selectedTemplate.id === 'allow-all-overrides' && (
                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-900">
                      All conversation configuration overrides will be enabled, allowing LLM, prompt, language, and text-only modifications.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Pick a quick action to configure it here.</p>
            )}

            {/* Preview */}
            {selectedTemplate && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-900">
                  <strong>Ready to update:</strong> {targetLabel} will be updated with these settings.
                </p>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="order-1 lg:order-2 space-y-4">
            <h3 className="font-semibold text-gray-900">Quick Actions</h3>
            {BULK_UPDATE_TEMPLATE_GROUPS.map((group) => (
              <div key={group} className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-700">{group}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {BULK_UPDATE_TEMPLATES.filter((template) => template.group === group).map((template) => {
                    const Icon = template.icon;
                    return (
                      <button
                        key={template.id}
                        onClick={() => handleTemplateSelect(template)}
                        className={`min-h-[44px] p-3 rounded-lg border-2 transition-colors text-left ${
                          selectedTemplate?.id === template.id
                            ? 'border-genie-primary bg-genie-primary/5'
                            : 'border-gray-200 hover:border-genie-primary/50'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <Icon className="h-4 w-4 text-genie-primary flex-shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900">{template.name}</p>
                            <p className="text-xs text-gray-600">{template.description}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 -mx-8 px-8 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] flex justify-start gap-3 border-t bg-white">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isApplying}
          >
            Cancel
          </Button>
          <Button
            onClick={() => selectedTemplate && onApplyUpdates({ templateId: selectedTemplate.id, updates: customUpdates, scope })}
            disabled={!selectedTemplate || !hasTarget || isMissingValue || isApplying}
            className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-lg"
          >
            {isApplying ? 'Applying...' : 'Apply Updates'}
          </Button>
        </div>
      </div>
    </CoreDrawer>
  );
};
